import { useState, useEffect } from 'react';
import { Tabs } from 'antd';
import type { TabsProps } from 'antd';
import styled from 'styled-components';
import { observer } from '@formily/reactive-react';
import { useField, useFieldSchema } from '@formily/react';
import type { VoidField } from '@formily/core';
import { ComponentType } from '../../types';
import type { IFormTabPaneProps } from '../form-tab-pane';
import FormTabPane from '../form-tab-pane';
import { sideTabsStyles } from '../../utils/sideTabsStyles';
import { FormTabsContext } from './context';

export interface IFormTabsProps {
  type?: TabsProps['type'];
  size?: TabsProps['size'];
  tabPosition?: TabsProps['tabPosition'];
  centered?: boolean;
  style?: React.CSSProperties;
}

const FormTabsBase = observer(
  ({ children }: { children?: React.ReactNode }) => {
    const field = useField<VoidField>();
    const schema = useFieldSchema();
    const [activeKey, setActiveKey] = useState<string>('');

    const tabPaneEntries = Object.entries(schema.properties || {})
      .filter(([, s]) => s['x-component'] === ComponentType.FormTabPane)
      .sort(
        (a, b) =>
          ((a[1]['x-index'] as number) ?? 0) -
          ((b[1]['x-index'] as number) ?? 0)
      );

    useEffect(() => {
      if (!activeKey && tabPaneEntries.length > 0) {
        setActiveKey(tabPaneEntries[0][0]);
      }
    }, []);

    const componentProps = (field?.componentProps || {}) as IFormTabsProps;

    const tabItems = tabPaneEntries.map(([key, s]) => ({
      key,
      label: (s['x-component-props'] as IFormTabPaneProps)?.tab || 'Tab',
      disabled: (s['x-component-props'] as IFormTabPaneProps)?.disabled,
    }));

    const tabPosition = componentProps.tabPosition;
    const isSidePosition = tabPosition === 'left' || tabPosition === 'right';
    const isBottomPosition = tabPosition === 'bottom';

    const flexDirection =
      tabPosition === 'right'
        ? 'row-reverse'
        : tabPosition === 'bottom'
          ? 'column-reverse'
          : 'row';

    return (
      <FormTabsContext.Provider value={{ activeKey }}>
        <div
          style={{
            ...componentProps.style,
            ...((isSidePosition || isBottomPosition) && {
              display: 'flex',
              flexDirection,
            }),
          }}
        >
          <StyledTabs
            type={componentProps.type}
            size={componentProps.size}
            tabPosition={tabPosition}
            centered={componentProps.centered}
            items={tabItems}
            activeKey={activeKey}
            onChange={setActiveKey}
          />
          <div
            style={
              isSidePosition || isBottomPosition
                ? { flex: 1, minWidth: 0 }
                : undefined
            }
          >
            {children}
          </div>
        </div>
      </FormTabsContext.Provider>
    );
  }
);

export default Object.assign(FormTabsBase, { TabPane: FormTabPane });

const StyledTabs = styled(Tabs)`
  ${sideTabsStyles}
`;
